/** Shared helpers for topic diagnostic “supporting pages” lists (Quick Compare + URL detail). */

export type TopicPageRole = 'main' | 'supporting';

export interface TopicSupportingPage {
  url: string;
  seoIntentScore: number;
  estimatedOrganicTraffic?: number;
}

export interface TopicPageChip {
  topicSlug: string;
  topicLabel: string;
  role: TopicPageRole;
}

export interface DomainTopicUrlRow {
  url: string;
  seoIntentScore: number;
  estimatedOrganicTraffic?: number;
  topics: TopicPageChip[];
  /** v1: best token overlap vs your site (competitor rows only). */
  similarOnYourSite?: { url: string; score: number };
}

export interface TopicSlugLabel {
  slug: string;
  label: string;
}

/** Minimal parent-topic row for slug resolution (domain topics compact API). */
export interface TopicParentLookup {
  parentTopicSlug: string;
  childTopicSlugs?: string[];
}

export interface ExtractTopicTaggedPagesOptions {
  /** Page-level topic slugs to keep; matches diagnostic bucket `parentTopicSlug` (child slug). */
  targetTopicSlugs: Iterable<string>;
  /** Preferred chip labels from page signals, keyed by normalized slug. */
  topicLabelsBySlug?: Map<string, string>;
  maxTotalPages?: number;
}

interface TopicTaggedPage extends TopicSupportingPage {
  topicSlug: string;
  topicLabel: string;
  role: TopicPageRole;
}

const DEFAULT_MIN_WEIGHT = 0.3;
const DEFAULT_TOP_N = 3;
const DEFAULT_MAX_PAGES_PER_DIAGNOSTIC = 25;

/**
 * Minimum SEO intent score (`seoIntentScore` from topic diagnostics) for a URL to appear
 * in URL detail “Supporting pages by topic”. Rows must be **strictly greater** than this value.
 */
export const SUPPORTING_PAGE_MIN_RELEVANCE_SCORE = 0.55;

/** Supporting-page rows shown per domain before “Show more pages” (dev view). */
export const SUPPORTING_PAGE_INITIAL_VISIBLE_COUNT = 5;

/** Supporting-page rows per domain column in user view before card expand. */
export const SUPPORTING_PAGE_USER_INITIAL_VISIBLE_COUNT = 3;

export function normalizeTopicSlug(slug: string): string {
  return slug.toLowerCase().trim();
}

/** Map a page signal slug to the parent topic slug used by domain diagnostics. */
export function resolveParentTopicSlug(topicSlug: string, parentTopics: TopicParentLookup[]): string {
  const normalized = normalizeTopicSlug(topicSlug);
  for (const topic of parentTopics) {
    const parent = normalizeTopicSlug(topic.parentTopicSlug);
    if (parent === normalized) return parent;
    if (
      Array.isArray(topic.childTopicSlugs) &&
      topic.childTopicSlugs.some((child) => normalizeTopicSlug(child) === normalized)
    ) {
      return parent;
    }
  }
  return normalized;
}

/** Unique parent slugs to fetch diagnostics for (deduped). */
export function uniqueParentDiagnosticSlugs(
  pageTopics: TopicSlugLabel[],
  parentTopics: TopicParentLookup[]
): string[] {
  const parents = new Set<string>();
  for (const topic of pageTopics) {
    parents.add(resolveParentTopicSlug(topic.slug, parentTopics));
  }
  return [...parents];
}

export function buildPageTopicLabelsBySlug(pageTopics: TopicSlugLabel[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const topic of pageTopics) {
    map.set(normalizeTopicSlug(topic.slug), topic.label);
  }
  return map;
}

/** Topics on the focal page used to narrow “your site” match pools (excludes weak secondary signals). */
export const FOCAL_ANCHOR_MIN_RELATIVE_WEIGHT = 0.85;
export const FOCAL_ANCHOR_MAX_TOPICS = 2;
export const FOCAL_ANCHOR_COMPARISON_MIN_RELATIVE_WEIGHT = 0.97;

const COMPARISON_URL_MARKERS = [
  ' vs ',
  '-vs-',
  '/vs-',
  '-versus-',
  '/versus',
  ' versus ',
  '-alternatives',
  '/alternatives',
  '-alternative/',
  '/alternative/',
  'alternatives-to',
  '-alternatives-to',
  'comparison',
  'compare ',
  '-compare-',
  '/compare/'
] as const;

export function isComparisonLikeUrlForTopicAnchors(url: string): boolean {
  const value = (url || '').toLowerCase();
  return COMPARISON_URL_MARKERS.some((marker) => value.includes(marker));
}

export function getFocalAnchorTopicSlugs(
  analysis: { topic?: { topicSignals?: unknown } } | null | undefined,
  options?: { minRelativeWeight?: number; maxTopics?: number }
): { slugs: Set<string>; labels: string[]; topics: TopicSlugLabel[] } {
  const signals: Array<{ topicSlug: string; label?: string; weight: number }> = (
    Array.isArray(analysis?.topic?.topicSignals) ? analysis!.topic!.topicSignals : []
  )
    .filter((s: any) => Number.isFinite(s?.weight) && s.weight > 0 && s?.topicSlug)
    .sort((a: any, b: any) => Number(b.weight) - Number(a.weight));

  if (!signals.length) {
    return { slugs: new Set(), labels: [], topics: [] };
  }

  const topWeight = signals[0].weight;
  const minW = topWeight * (options?.minRelativeWeight ?? FOCAL_ANCHOR_MIN_RELATIVE_WEIGHT);
  const maxTopics = options?.maxTopics ?? FOCAL_ANCHOR_MAX_TOPICS;
  const picked = signals.filter((s) => s.weight >= minW).slice(0, maxTopics);

  const topics: TopicSlugLabel[] = picked.map((s) => ({
    slug: String(s.topicSlug),
    label: typeof s.label === 'string' && s.label.trim() ? s.label.trim() : String(s.topicSlug)
  }));
  const slugs = new Set(topics.map((t) => normalizeTopicSlug(t.slug)));
  const labels = topics.map((t) => t.label);
  return { slugs, labels, topics };
}

export function rowOverlapsFocalAnchorTopics(
  row: DomainTopicUrlRow,
  anchorSlugs: Set<string>
): boolean {
  if (!anchorSlugs.size) return true;
  return row.topics.some((chip) => anchorSlugs.has(normalizeTopicSlug(chip.topicSlug)));
}

export function filterRowsByFocalAnchorTopics(
  rows: DomainTopicUrlRow[],
  anchorSlugs: Set<string>
): DomainTopicUrlRow[] {
  if (!anchorSlugs.size) return rows;
  return rows.filter((row) => rowOverlapsFocalAnchorTopics(row, anchorSlugs));
}

/** Your-site candidates that share focal anchor + at least one topic chip with the competitor row. */
export function filterYourMatchPoolForCompetitorRow(
  yourRows: DomainTopicUrlRow[],
  competitorRow: DomainTopicUrlRow,
  anchorSlugs: Set<string>
): DomainTopicUrlRow[] {
  const anchorPool = filterRowsByFocalAnchorTopics(yourRows, anchorSlugs);
  if (!anchorPool.length) return [];

  const compSlugs = new Set(
    competitorRow.topics.map((c) => normalizeTopicSlug(c.topicSlug)).filter(Boolean)
  );
  if (!compSlugs.size) return anchorPool;

  return anchorPool.filter((row) =>
    row.topics.some((c) => compSlugs.has(normalizeTopicSlug(c.topicSlug)))
  );
}

export function getTopTopicsForSupportingPages(
  analysis: { topic?: { topicSignals?: unknown } } | null | undefined,
  options?: { minWeight?: number; limit?: number }
): TopicSlugLabel[] {
  const minWeight = options?.minWeight ?? DEFAULT_MIN_WEIGHT;
  const limit = options?.limit ?? DEFAULT_TOP_N;
  const signals: any[] = Array.isArray(analysis?.topic?.topicSignals) ? analysis!.topic!.topicSignals : [];
  return signals
    .filter((s: any) => Number.isFinite(s?.weight) && Number(s.weight) >= minWeight && s?.topicSlug)
    .sort((a: any, b: any) => Number(b.weight) - Number(a.weight))
    .slice(0, limit)
    .map((s: any) => ({
      slug: String(s.topicSlug),
      label: typeof s.label === 'string' && s.label.trim() ? s.label.trim() : String(s.topicSlug)
    }));
}

/**
 * Pages from a parent-topic diagnostic, one chip per matching child bucket (core / mention).
 */
export function extractTopicTaggedPagesFromDiagnostic(
  diagnosticResponse: unknown,
  options: ExtractTopicTaggedPagesOptions
): TopicTaggedPage[] {
  const res = diagnosticResponse as { data?: { coverage?: { directlyCoveredTopics?: unknown[] } } } | null;
  if (!res?.data?.coverage?.directlyCoveredTopics) return [];

  const targetSlugs = new Set(
    [...options.targetTopicSlugs].map((s) => normalizeTopicSlug(s)).filter(Boolean)
  );
  if (targetSlugs.size === 0) return [];

  const maxTotal = options.maxTotalPages ?? DEFAULT_MAX_PAGES_PER_DIAGNOSTIC;
  const topics: any[] = res.data.coverage.directlyCoveredTopics;
  const tagged: TopicTaggedPage[] = [];

  for (const bucket of topics) {
    const bucketSlug = normalizeTopicSlug(String(bucket?.parentTopicSlug || ''));
    if (!bucketSlug || !targetSlugs.has(bucketSlug)) continue;

    const bucketLabel =
      options.topicLabelsBySlug?.get(bucketSlug) ||
      (typeof bucket?.label === 'string' && bucket.label.trim() ? bucket.label.trim() : bucketSlug);

    const pushPage = (p: any, role: TopicPageRole) => {
      if (!p?.url) return;
      tagged.push({
        url: String(p.url),
        seoIntentScore: Number.isFinite(p.seoIntentScore) ? Number(p.seoIntentScore) : 0,
        estimatedOrganicTraffic:
          typeof p.estimatedOrganicTraffic === 'number' ? p.estimatedOrganicTraffic : undefined,
        topicSlug: bucketSlug,
        topicLabel: bucketLabel,
        role
      });
    };

    for (const p of Array.isArray(bucket.corePages) ? bucket.corePages : []) {
      pushPage(p, 'main');
    }
    for (const p of Array.isArray(bucket.mentionPages) ? bucket.mentionPages : []) {
      pushPage(p, 'supporting');
    }
  }

  return tagged.sort((a, b) => b.seoIntentScore - a.seoIntentScore).slice(0, maxTotal);
}

export function passesSupportingPageRelevanceThreshold(seoIntentScore: number): boolean {
  return Number.isFinite(seoIntentScore) && seoIntentScore > SUPPORTING_PAGE_MIN_RELEVANCE_SCORE;
}

/** Keep supporting-page rows whose relevance exceeds {@link SUPPORTING_PAGE_MIN_RELEVANCE_SCORE}. */
export function filterSupportingPagesByRelevance(rows: DomainTopicUrlRow[]): DomainTopicUrlRow[] {
  return rows.filter((row) => passesSupportingPageRelevanceThreshold(row.seoIntentScore));
}

/** One row per URL with topic chips (deduped by slug + role). */
export function mergeTopicTaggedPagesByUrl(
  pages: TopicTaggedPage[],
  maxRows?: number
): DomainTopicUrlRow[] {
  const byUrl = new Map<string, DomainTopicUrlRow>();

  for (const page of pages) {
    const key = normalizeUrlForCompare(page.url);
    let row = byUrl.get(key);
    if (!row) {
      row = {
        url: page.url,
        seoIntentScore: page.seoIntentScore,
        estimatedOrganicTraffic: page.estimatedOrganicTraffic,
        topics: []
      };
      byUrl.set(key, row);
    }

    if (page.seoIntentScore > row.seoIntentScore) {
      row.seoIntentScore = page.seoIntentScore;
      row.estimatedOrganicTraffic = page.estimatedOrganicTraffic;
    }

    const chipKey = `${page.topicSlug}::${page.role}`;
    if (!row.topics.some((c) => `${c.topicSlug}::${c.role}` === chipKey)) {
      row.topics.push({
        topicSlug: page.topicSlug,
        topicLabel: page.topicLabel,
        role: page.role
      });
    }
  }

  const sortChips = (chips: TopicPageChip[]) =>
    chips.sort((a, b) => {
      if (a.role !== b.role) return a.role === 'main' ? -1 : 1;
      return a.topicLabel.localeCompare(b.topicLabel);
    });

  const merged = [...byUrl.values()]
    .map((row) => ({ ...row, topics: sortChips(row.topics) }))
    .sort((a, b) => b.seoIntentScore - a.seoIntentScore);
  if (maxRows != null && maxRows > 0) {
    return merged.slice(0, maxRows);
  }
  return merged;
}

/** @deprecated Use extractTopicTaggedPagesFromDiagnostic + merge for URL detail ownership. */
export function extractSupportingPagesFromDiagnostic(
  diagnosticResponse: unknown,
  maxPages = 5
): TopicSupportingPage[] {
  const res = diagnosticResponse as { data?: { coverage?: { directlyCoveredTopics?: unknown[] } } } | null;
  if (!res?.data?.coverage?.directlyCoveredTopics) return [];
  const topics: any[] = res.data.coverage.directlyCoveredTopics;
  const allUrls: TopicSupportingPage[] = [];
  for (const t of topics) {
    const pages = [
      ...(Array.isArray(t.corePages) ? t.corePages : []),
      ...(Array.isArray(t.mentionPages) ? t.mentionPages : [])
    ];
    for (const p of pages) {
      if (p?.url) {
        allUrls.push({
          url: String(p.url),
          seoIntentScore: Number.isFinite(p.seoIntentScore) ? Number(p.seoIntentScore) : 0,
          estimatedOrganicTraffic:
            typeof p.estimatedOrganicTraffic === 'number' ? p.estimatedOrganicTraffic : undefined
        });
      }
    }
  }
  return allUrls.sort((a, b) => b.seoIntentScore - a.seoIntentScore).slice(0, maxPages);
}

export function formatSupportingPagePath(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname || '/';
  } catch {
    return url;
  }
}

export function formatSupportingPageTraffic(v: number | undefined): string {
  if (!Number.isFinite(v) || v === undefined || v === null) return '';
  if (v >= 1000) return `~${(v / 1000).toFixed(1)}k/mo`;
  return `~${Math.round(v)}/mo`;
}

export function normalizeUrlForCompare(a: string): string {
  try {
    const u = new URL(a.trim());
    u.hash = '';
    let path = u.pathname.replace(/\/+$/, '') || '/';
    return `${u.origin}${path}`.toLowerCase();
  } catch {
    return a.trim().toLowerCase();
  }
}
